import { useCallback } from "react";
import { PressEvent } from "react-aria";
import { useAtom, useSetAtom } from "jotai";
import { Button } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  floatingToolbarActiveButtonAtom,
  sidebarOpenNodeIdAtom,
} from "@zapier/canvas-state";
import styles from "./NotesButton.module.css";

type Props = {
  id: string;
  hasNotes: boolean;
  isViewOnlyMode: boolean;
};

export function NotesButton({ id, hasNotes, isViewOnlyMode }: Props) {
  const setActiveButton = useSetAtom(floatingToolbarActiveButtonAtom);
  const [sidebarOpenNodeId, setSidebarOpenNodeId] = useAtom(
    sidebarOpenNodeIdAtom,
  );

  const onNotesButtonClick = useCallback(
    (e: PressEvent) => {
      e.continuePropagation(); // Button won't toggle without this
      const isAlreadyOpen = sidebarOpenNodeId === id;

      /* The timeout ensures our setSidebarOpenNodeId happens after shouldCloseSidebarOnDeselect
       * clears it, preventing the sidebar from closing immediately when switching between nodes. */
      setTimeout(() => {
        setSidebarOpenNodeId(isAlreadyOpen ? "" : id);
        setActiveButton(isAlreadyOpen ? null : "note");
      }, 0);
    },
    [id, setActiveButton, setSidebarOpenNodeId, sidebarOpenNodeId],
  );

  if (!hasNotes) {
    return null;
  }

  return (
    <div className={styles.notesButton}>
      <Button
        variant="tertiary"
        size="compact"
        onPress={onNotesButtonClick}
        isDisabled={isViewOnlyMode}
        iconPosition="start"
      >
        <Icon name="fileFilled" />
        Notes
      </Button>
    </div>
  );
}
